'use client'

import { useState } from 'react'
import { authClient } from '@/lib/auth/client'

interface OAuthButtonsProps {
  onError?: (msg: string) => void
  accountType?: 'individual' | 'company'
}

export function OAuthButtons({ onError, accountType }: OAuthButtonsProps) {
  const [loading, setLoading] = useState<'google' | 'github' | null>(null)

  async function handleOAuth(provider: 'google' | 'github') {
    setLoading(provider)
    try {
      // Persist account_type in a short-lived cookie so the post-OAuth
      // hook / callback flow can apply it to the profile after the
      // provider returns (providers overwrite metadata).
      //
      // Only write the cookie when an explicit accountType was provided
      // by the parent (the /signup form passes the toggle value).
      // Pages that DON'T specify intent (notably /login, which is a
      // login surface, not a signup surface) must NOT force the cookie
      // to 'individual' - that stranded new-via-login OAuth users on
      // an individual account with no way to switch. Without the
      // cookie, the handle_new_user() SQL trigger applies its
      // 'individual' default, which is the right behaviour for /login
      // (existing users keep their type; new users default to
      // individual and can switch via /onboarding/company).
      if (accountType) {
        const secure = window.location.protocol === 'https:' ? '; Secure' : ''
        document.cookie = `pv_account_type=${accountType}; path=/; max-age=600; SameSite=Lax${secure}`
      }

      const { error } = await authClient.signIn.social({
        provider,
        callbackURL: '/feed',
        // A NEW OAuth account is routed here so OAuthSignupTracker can fire the
        // sign_up event (SEO plan 0.7); returning users go to callbackURL and
        // are not miscounted. Individual new users land on /onboarding with the
        // marker intact.
        newUserCallbackURL: `/onboarding?signup=${provider}`,
      })
      if (error) {
        onError?.('Authentication failed. Try again')
        setLoading(null)
      }
    } catch {
      onError?.('Authentication failed. Try again')
      setLoading(null)
    }
  }

  const disabled = loading !== null

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={() => handleOAuth('google')}
        disabled={disabled}
        aria-label="Continue with Google"
        className="flex h-12 w-full items-center justify-center gap-3 rounded-[var(--radius-md)] border border-[var(--color-text-muted)] bg-transparent text-sm font-medium text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-bg-elevated)] disabled:cursor-default disabled:opacity-50"
        style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
      >
        {loading === 'google' ? (
          <>
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--color-text-muted)] border-t-[var(--color-text-primary)]" />
            Connecting...
          </>
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </>
        )}
      </button>

      <button
        type="button"
        onClick={() => handleOAuth('github')}
        disabled={disabled}
        aria-label="Continue with GitHub"
        className="flex h-12 w-full items-center justify-center gap-3 rounded-[var(--radius-md)] border border-[var(--color-text-muted)] bg-transparent text-sm font-medium text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-bg-elevated)] disabled:cursor-default disabled:opacity-50"
        style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
      >
        {loading === 'github' ? (
          <>
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--color-text-muted)] border-t-[var(--color-text-primary)]" />
            Connecting...
          </>
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white" aria-hidden="true">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
            </svg>
            Continue with GitHub
          </>
        )}
      </button>
    </div>
  )
}
